import { CustomSurvey } from "../../models"
import { useMemo } from "react"
import { ICustomSurveyFormData } from "../../components"
import { validators } from "./validators"
import { useUncommittedChanges } from "../../../../UncommitedChanges"

interface ValidatedField {
  wording: string,
  isValid: boolean,
  isDirty: boolean,
}

export const useCustomSurveyValidation = (surveyData: ICustomSurveyFormData, customSurvey?: CustomSurvey) => {
  const { setUncommittedChange, removeUncommittedChange } = useUncommittedChanges<ValidatedField[]>("customSurvey")

  return useMemo(() => {
    let isDirty = false
    let isValid = true

    const fields = validators.reduce((validatedFields, validator) => {
      const isFieldValid = validator.isRequired ? validator.isValid(surveyData) : true
      const isFieldDirty = validator.isDirty(surveyData, customSurvey)

      if (!isFieldValid) {
        isValid = false
      }

      if (isFieldDirty) {
        isDirty = true
      }

      if (validator.isRequired) {
        validatedFields.push({
          isDirty: isFieldValid,
          isValid: isFieldValid,
          wording: validator.wording(),
        })
      }

      return validatedFields
    }, new Array<ValidatedField>())

    if (isDirty) {
      setUncommittedChange(fields)
    }
    else {
      removeUncommittedChange()
    }

    const isSurveyUsed = !!customSurvey?.nextUsedAt || !!customSurvey?.lastUsedAt
    const isEditMode = !!customSurvey
    const isSurveyImmutable = getIsSurveyImmutable(isValid, isDirty, isEditMode, isSurveyUsed)
    const isSurveyDeletable = isEditMode && !isSurveyUsed

    return {
      fields,
      isDirty,
      isEditMode,
      isSurveyDeletable,
      isSurveyImmutable,
      isSurveyUsed,
      isValid,
    }
  }, [ surveyData, customSurvey, setUncommittedChange, removeUncommittedChange ])
}

function getIsSurveyImmutable(
  isValid: boolean,
  isDirty: boolean,
  isSurveyUpdate: boolean,
  isSurveyUsed: boolean,
): boolean {
  if (isSurveyUsed) {
    return true
  }

  if (isSurveyUpdate) {
    return !isValid || !isDirty
  }

  return !isValid
}
